/**
 * Calendar Types for Operator Dashboard Booking Calendar
 *
 * These types support the booking calendar visualization in the operator dashboard,
 * providing type-safe interfaces for calendar views, bookings, and status management.
 */

// ============================================================================
// Enums & Constants
// ============================================================================

/**
 * Booking status values matching the domain model
 */
export type BookingStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'IN_PROGRESS'
  | 'NO_SHOW';

/**
 * Payment status values matching the domain model
 */
export type PaymentStatus =
  | 'PENDING'
  | 'PAID'
  | 'FAILED'
  | 'REFUNDED'
  | 'WAIVED';

/**
 * Available calendar view types
 */
export type CalendarViewType = 'day' | 'week' | 'month' | 'agenda';

// ============================================================================
// Color Constants for Status Visualization
// ============================================================================

/**
 * Color configuration for booking status visualization
 * Each status has background, text, and border colors for consistent UI
 */
export const BOOKING_STATUS_COLORS = {
  PENDING: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    dot: 'bg-amber-400',
    hex: '#d97706',  // Muted amber (was #f59e0b)
  },
  CONFIRMED: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-200',
    dot: 'bg-sky-400',
    hex: '#0284c7',  // Muted sky blue (was #3b82f6)
  },
  IN_PROGRESS: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-200',
    dot: 'bg-violet-400',
    hex: '#7c3aed',  // Muted violet (was #8b5cf6)
  },
  COMPLETED: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    dot: 'bg-emerald-400',
    hex: '#059669',  // Muted emerald (was #22c55e)
  },
  CANCELLED: {
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    border: 'border-slate-200',
    dot: 'bg-slate-400',
    hex: '#64748b',  // Slate (was #6b7280)
  },
  NO_SHOW: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-200',
    dot: 'bg-rose-400',
    hex: '#e11d48',  // Muted rose (was #ef4444)
  },
} as const;

/**
 * Color configuration for payment status visualization
 */
export const PAYMENT_STATUS_COLORS = {
  PENDING: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    hex: '#d97706',  // Muted amber
  },
  PAID: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    hex: '#059669',  // Muted emerald
  },
  FAILED: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-200',
    hex: '#e11d48',  // Muted rose
  },
  REFUNDED: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    hex: '#c2410c',  // Muted orange
  },
  WAIVED: {
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    border: 'border-slate-200',
    hex: '#475569',  // Slate
  },
} as const;

// ============================================================================
// Core Calendar Types
// ============================================================================

/**
 * Booking data structured for calendar display
 */
export interface CalendarBooking {
  /** Unique booking identifier */
  id: string;

  /** Business information */
  businessId: string;
  businessName: string;

  /** Service information */
  serviceId: string;
  serviceName: string;

  /** Customer information */
  customerName: string;
  customerId?: string;
  customerEmail?: string;
  customerPhone?: string;

  /** Scheduling */
  scheduledAt: Date;
  endsAt: Date;

  /** Duration in minutes (computed from scheduledAt and endsAt) */
  durationMinutes: number;

  /** Status tracking */
  status: BookingStatus;
  paymentStatus: PaymentStatus;

  /** Additional information */
  notes?: string;

  /** Price information */
  price?: number;
  currency?: string;

  /** Staff assignment */
  staffId?: string;
  staffName?: string;

  /** Metadata */
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * Time slot representation for calendar grid
 */
export interface CalendarTimeSlot {
  /** Start time of the slot */
  startTime: Date;

  /** End time of the slot */
  endTime: Date;

  /** Whether this slot is available for booking */
  isAvailable: boolean;

  /** Whether this slot is in the past */
  isPast: boolean;

  /** Whether this slot is currently selected */
  isSelected?: boolean;

  /** Bookings that overlap with this time slot */
  bookings: CalendarBooking[];

  /** Display label (e.g., "9:00 AM") */
  label: string;
}

/**
 * Date range for calendar view navigation
 */
export interface CalendarDateRange {
  /** Start date of the visible range */
  start: Date;

  /** End date of the visible range */
  end: Date;
}

/**
 * Type for booking status color configuration
 */
export type BookingStatusColors = typeof BOOKING_STATUS_COLORS;

/**
 * Type for payment status color configuration
 */
export type PaymentStatusColors = typeof PAYMENT_STATUS_COLORS;

// ============================================================================
// Calendar State & Configuration Types
// ============================================================================

/**
 * Calendar view state for managing the calendar component
 */
export interface CalendarViewState {
  /** Current view type */
  viewType: CalendarViewType;

  /** Currently selected/focused date */
  selectedDate: Date;

  /** Visible date range based on view type */
  dateRange: CalendarDateRange;

  /** Filter by booking status */
  statusFilter?: BookingStatus[];

  /** Filter by business */
  businessFilter?: string[];

  /** Filter by staff member */
  staffFilter?: string[];
}

/**
 * Calendar configuration options
 */
export interface CalendarConfig {
  /** Start hour of the day view (0-23) */
  dayStartHour: number;

  /** End hour of the day view (0-23) */
  dayEndHour: number;

  /** Time slot interval in minutes */
  slotIntervalMinutes: number;

  /** First day of the week (0 = Sunday, 1 = Monday, etc.) */
  weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6;

  /** Whether to show weekends in week/month view */
  showWeekends: boolean;

  /** Timezone for display */
  timezone: string;
}

/**
 * Default calendar configuration
 */
export const DEFAULT_CALENDAR_CONFIG: CalendarConfig = {
  dayStartHour: 8,
  dayEndHour: 20,
  slotIntervalMinutes: 30,
  weekStartsOn: 1, // Monday
  showWeekends: true,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

// ============================================================================
// Event & Callback Types
// ============================================================================

/**
 * Calendar event handlers
 */
export interface CalendarEventHandlers {
  /** Called when a booking is clicked */
  onBookingClick?: (booking: CalendarBooking) => void;

  /** Called when an empty time slot is clicked */
  onSlotClick?: (slot: CalendarTimeSlot) => void;

  /** Called when the view type changes */
  onViewChange?: (viewType: CalendarViewType) => void;

  /** Called when the date range changes (navigation) */
  onDateRangeChange?: (range: CalendarDateRange) => void;

  /** Called when a booking is dragged to a new time */
  onBookingDrop?: (booking: CalendarBooking, newStart: Date) => void;

  /** Called when a booking is resized */
  onBookingResize?: (booking: CalendarBooking, newEnd: Date) => void;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Grouped bookings by date for agenda view
 */
export interface BookingsByDate {
  [dateKey: string]: CalendarBooking[];
}

/**
 * Calendar day cell data for month view
 */
export interface CalendarDayCell {
  /** The date this cell represents */
  date: Date;

  /** Whether this date is in the current month */
  isCurrentMonth: boolean;

  /** Whether this is today */
  isToday: boolean;

  /** Whether this date is selected */
  isSelected: boolean;

  /** Bookings for this date */
  bookings: CalendarBooking[];

  /** Total count of bookings (for overflow display) */
  bookingCount: number;
}

/**
 * Booking summary for quick stats
 */
export interface BookingSummary {
  total: number;
  byStatus: Record<BookingStatus, number>;
  byPaymentStatus: Record<PaymentStatus, number>;
  revenue: number;
}

/**
 * Staff color for calendar visualization
 */
export interface StaffColor {
  /** Primary color for events */
  primary: string;
  /** Light background variant */
  background: string;
  /** Border color */
  border: string;
  /** Text color for contrast */
  text: string;
}

/**
 * Predefined staff colors for multi-staff calendars
 * Designed for visual distinction and accessibility
 */
export const STAFF_COLORS: StaffColor[] = [
  { primary: '#3b82f6', background: '#dbeafe', border: '#93c5fd', text: '#1e40af' }, // Blue
  { primary: '#10b981', background: '#d1fae5', border: '#6ee7b7', text: '#065f46' }, // Emerald
  { primary: '#f59e0b', background: '#fef3c7', border: '#fcd34d', text: '#92400e' }, // Amber
  { primary: '#ef4444', background: '#fee2e2', border: '#fca5a5', text: '#991b1b' }, // Red
  { primary: '#8b5cf6', background: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' }, // Violet
  { primary: '#ec4899', background: '#fce7f3', border: '#f9a8d4', text: '#9d174d' }, // Pink
  { primary: '#06b6d4', background: '#cffafe', border: '#67e8f9', text: '#0e7490' }, // Cyan
  { primary: '#f97316', background: '#ffedd5', border: '#fdba74', text: '#9a3412' }, // Orange
];

/**
 * Get a staff color by index (cycles through available colors)
 */
export function getStaffColor(index: number): StaffColor {
  return STAFF_COLORS[index % STAFF_COLORS.length];
}

/**
 * Bookings grouped by staff member for multi-staff views
 */
export interface BookingsByStaff {
  [staffId: string]: CalendarBooking[];
}
